/**
 * Conflict Resolution Service
 * 
 * Implements last-write-wins strategy for simultaneous edits
 * Requirements: 11.1, 11.2, 11.3
 */

export type ConflictType = 
  | 'channel_edit'
  | 'pattern_edit'
  | 'mixer_edit'
  | 'note_edit'
  | 'automation_edit'
  | 'clip_edit'
  | 'channel_delete'
  | 'track_delete';

export interface ConflictEvent {
  id: string;
  type: ConflictType;
  timestamp: number;
  userId: string;
  userName: string;
  elementId: string;
  elementName: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

export interface EditOperation {
  id: string;
  type: ConflictType;
  elementId: string;
  elementName: string;
  userId: string;
  userName: string;
  timestamp: number;
  data: any;
}

/**
 * Tracks recent edit operations to detect conflicts
 */
class ConflictDetector {
  private recentEdits: Map<string, EditOperation> = new Map();
  private conflictWindow = 2000; // 2 seconds window for conflict detection
  private conflicts: ConflictEvent[] = [];
  
  /**
   * Register an edit operation
   * Returns conflict event if simultaneous edit detected
   */
  registerEdit(operation: EditOperation): ConflictEvent | null {
    const key = `${operation.type}:${operation.elementId}`;
    const existingEdit = this.recentEdits.get(key);
    
    // Check for simultaneous edit (Requirements 11.1)
    if (existingEdit && existingEdit.userId !== operation.userId) {
      const timeDiff = operation.timestamp - existingEdit.timestamp;
      
      // If edit occurred within conflict window, it's a conflict
      if (timeDiff < this.conflictWindow) {
        const conflict = this.createConflictEvent(existingEdit, operation);
        this.conflicts.push(conflict);
        
        // Log conflict (Requirements 11.3)
        console.warn('Conflict detected:', conflict);
        
        return conflict;
      }
    }
    
    // Store this edit
    this.recentEdits.set(key, operation);
    
    // Clean up old edits
    this.cleanupOldEdits();
    
    return null;
  }
  
  /**
   * Create conflict event from two simultaneous edits
   */
  private createConflictEvent(
    firstEdit: EditOperation,
    secondEdit: EditOperation
  ): ConflictEvent {
    // Determine severity based on operation type (Requirements 11.4)
    const severity = this.determineSeverity(secondEdit.type);
    
    return {
      id: `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: secondEdit.type,
      timestamp: secondEdit.timestamp,
      userId: secondEdit.userId,
      userName: secondEdit.userName,
      elementId: secondEdit.elementId,
      elementName: secondEdit.elementName,
      description: `${secondEdit.userName} edited ${secondEdit.elementName} while ${firstEdit.userName} was also editing it`,
      severity,
    };
  }
  
  /**
   * Determine conflict severity
   */
  private determineSeverity(type: ConflictType): 'low' | 'medium' | 'high' {
    // Critical conflicts (Requirements 11.4)
    if (type === 'channel_delete' || type === 'track_delete') {
      return 'high';
    }
    
    // Medium severity conflicts
    if (type === 'mixer_edit' || type === 'automation_edit') {
      return 'medium';
    }
    
    // Low severity conflicts
    return 'low';
  }
  
  /**
   * Clean up edits older than conflict window
   */
  private cleanupOldEdits() {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.recentEdits.forEach((edit, key) => {
      if (now - edit.timestamp > this.conflictWindow * 2) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.recentEdits.delete(key));
  }
  
  /**
   * Get recent conflicts
   */
  getRecentConflicts(limit: number = 20): ConflictEvent[] {
    return this.conflicts.slice(-limit);
  }
  
  /**
   * Clear conflict history
   */
  clearConflicts() {
    this.conflicts = [];
  }
}

// Singleton instance
export const conflictDetector = new ConflictDetector();

/**
 * Apply last-write-wins strategy (Requirements 11.1)
 * Returns the operation that should be applied
 */
export function resolveConflict(
  localOperation: EditOperation,
  remoteOperation: EditOperation
): EditOperation {
  // Last-write-wins: operation with later timestamp wins
  if (remoteOperation.timestamp > localOperation.timestamp) {
    return remoteOperation;
  }
  
  // If timestamps are equal, use userId as tiebreaker
  if (remoteOperation.timestamp === localOperation.timestamp) {
    return remoteOperation.userId > localOperation.userId 
      ? remoteOperation 
      : localOperation;
  }
  
  return localOperation;
}

/**
 * Create notification message for conflict (Requirements 11.2)
 */
export function createConflictNotification(conflict: ConflictEvent): string {
  const severityEmoji = {
    low: '⚠️',
    medium: '⚠️',
    high: '🚨',
  };
  
  const emoji = severityEmoji[conflict.severity];
  
  if (conflict.severity === 'high') {
    return `${emoji} Critical Conflict: ${conflict.description}. Manual resolution may be required.`;
  }
  
  return `${emoji} Conflict: ${conflict.description}. Last edit was applied.`;
}

/**
 * Check if operation requires manual resolution (Requirements 11.4)
 */
export function requiresManualResolution(conflict: ConflictEvent): boolean {
  return conflict.severity === 'high';
}
